import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CarouselArrow } from "./CarouselArrow.js";
import type { Photo } from "../data/types.js";

interface Props {
  photos: Photo[];
  onPhotoClick: (index: number) => void;
}

export function PhotoCarousel({ photos, onPhotoClick }: Props) {
  const [index, setIndex] = useState(0);

  if (!photos || photos.length === 0) {
    return (
      <div className="flex flex-col flex-1 min-h-0 items-center justify-center">
        <p className="text-ink-2 text-sm">📷</p>
        <p className="text-ink-3 text-xs mt-1">—</p>
      </div>
    );
  }

  const wrapped = ((index % photos.length) + photos.length) % photos.length;
  const setWrapped = (i: number) => {
    setIndex(((i % photos.length) + photos.length) % photos.length);
  };
  const photo = photos[wrapped];

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="relative overflow-hidden flex-1 min-h-0">
        {photos.length > 1 && (
          <>
            <CarouselArrow direction="left" onClick={() => setWrapped(index - 1)} />
            <CarouselArrow direction="right" onClick={() => setWrapped(index + 1)} />
          </>
        )}
        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            key={wrapped}
            initial={{ scale: 0.98, opacity: 0.6 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute inset-0 flex items-center justify-center cursor-pointer"
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.4}
            onDragEnd={(_e, info) => {
              if (info.offset.x < -30 || info.velocity.x < -200) {
                setWrapped(index + 1);
              } else if (info.offset.x > 30 || info.velocity.x > 200) {
                setWrapped(index - 1);
              }
            }}
            onClick={() => onPhotoClick(wrapped)}
          >
            <img
              src={photo.url}
              alt={photo.alt}
              className="max-w-full max-h-full object-contain"
              draggable={false}
            />
          </motion.div>
        </AnimatePresence>
      </div>
      {photos.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 pt-2.5 pb-1">
          {photos.map((_, i) => (
            <button
              key={i}
              onClick={() => setWrapped(i)}
              className={`rounded-full transition-all ${
                i === wrapped ? "w-1.5 h-1.5 bg-kra" : "w-1 h-1 bg-ink-3/30"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
